import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { MdClose } from "react-icons/md";
import { Search } from "lucide-react";
import CheckTile from "src/Components/Checkbox/CheckTile";
import Loader from "src/Components/Common/Loader";

// Roles aren't present on the lightweight picker endpoint, so fall back to the
// email — same rule the older modal used.
const subtitleOf = (user) => user.roles?.[0]?.name ?? user.email;

const ReceiversDrawer = ({ open, context, userArray, selectedUserIdArray, saving, onCancel, onSave }) => {
  // `mounted` keeps the panel in the tree for the length of the close
  // animation; `shown` drives the transform so it animates on the frame after
  // mount rather than snapping into place.
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const [selectedOnly, setSelectedOnly] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      return undefined;
    }
    setShown(false);
    // Outlasts the 300ms transition so the panel isn't torn out mid-slide.
    const timer = setTimeout(() => setMounted(false), 320);
    return () => clearTimeout(timer);
  }, [open]);

  // Opening the slide has to wait for the panel to exist. Scheduling it
  // alongside `setMounted` above raced React's commit: if the render landed in
  // a scheduler task the frames fired first, so the panel mounted with `shown`
  // already true and snapped open with nothing to animate from.
  useLayoutEffect(() => {
    if (!open || !mounted || !panelRef.current) return undefined;
    // Reading a layout property resolves the off-screen `translate-x-full` as
    // the transition's start value; the frame after that is what it animates
    // away from.
    void panelRef.current.offsetWidth;
    const frame = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(frame);
  }, [open, mounted]);

  // Reset the working selection every time the drawer is opened on a rule, so
  // an abandoned edit never leaks into the next one.
  useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set((selectedUserIdArray ?? []).map(String)));
    setQuery("");
    setSelectedOnly(false);
  }, [open, selectedUserIdArray]);

  const visibleUsers = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return userArray.filter((user) => {
      if (selectedOnly && !selectedIds.has(String(user.id))) return false;
      if (!needle) return true;
      return `${user.name ?? ""} ${user.email ?? ""}`.toLowerCase().includes(needle);
    });
  }, [userArray, query, selectedOnly, selectedIds]);

  const visibleSelectedCount = visibleUsers.filter((user) => selectedIds.has(String(user.id))).length;
  const allVisibleSelected = visibleUsers.length > 0 && visibleSelectedCount === visibleUsers.length;
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;

  const toggleUser = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // "Select all" acts on what's currently visible, so it stays predictable
  // while a search or the Selected Only filter is narrowing the list.
  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      visibleUsers.forEach((user) => {
        if (allVisibleSelected) next.delete(String(user.id));
        else next.add(String(user.id));
      });
      return next;
    });
  };

  if (!mounted) return null;

  const save = () => onSave(userArray.filter((user) => selectedIds.has(String(user.id))));
  const crumbs = context?.crumbs ?? [];

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div
        onClick={onCancel}
        className={`absolute inset-0 bg-black transition-opacity duration-300 ease-out ${
          shown ? "bg-opacity-30" : "bg-opacity-0"
        }`}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Edit receivers"
        className={`relative w-[480px] max-w-full h-full bg-white border-l-[1px] border-[#E5E8F3] shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)] flex flex-col transition-transform duration-300 [transition-timing-function:cubic-bezier(0.32,0.72,0,1)] ${
          shown ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="p-6 border-b-[1px] border-[#E5E8F3]">
          <div className="flex items-start gap-4">
            <div className="flex-1 flex flex-col gap-[2px] min-w-0">
              <span className="font-OpenSansSemiBold text-[18px] leading-[28px] capitalize text-[#1A2332]">
                {selectedUserIdArray?.length ? "Edit Receivers" : "Add Receivers"}
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                {crumbs.map((crumb, index) => (
                  <span key={crumb} className="flex items-center gap-2">
                    {index > 0 && (
                      <span aria-hidden="true" className="font-OpenSansRegular text-[16px] leading-[24px] text-[#B4BAD5]">
                        •
                      </span>
                    )}
                    <span className="font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#868DA6]">
                      {crumb}
                    </span>
                  </span>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={onCancel}
              aria-label="Close"
              className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-[#868DA6] hover:text-[#1A2332] transition-colors"
            >
              <MdClose size={20} />
            </button>
          </div>
        </div>

        {/* Search + filters */}
        <div className="px-6 py-4 bg-white border-b-[1px] border-[#E5E8F3] flex flex-col gap-3">
          <div className="h-10 px-3 flex items-center gap-2 bg-white rounded-[8px] outline outline-1 outline-[#C9CEE2] -outline-offset-1 focus-within:outline-blueColor">
            <Search size={18} className="shrink-0 text-[#68778D]" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search users…"
              className="flex-1 min-w-0 bg-transparent font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] placeholder:text-[#868DA6] focus:outline-none"
            />
          </div>
          <div className="flex items-center gap-2">
            <FilterPill label="All roles" active={!selectedOnly} onClick={() => setSelectedOnly(false)} />
            <FilterPill label="Selected only" active={selectedOnly} onClick={() => setSelectedOnly(true)} />
          </div>
        </div>

        {/* Select all */}
        <div
          onClick={visibleUsers.length ? toggleAllVisible : undefined}
          className={`px-6 py-2 bg-[#FAFBFC] border-b-[0.8px] border-[#E2E6EA] flex items-center gap-3 ${
            visibleUsers.length ? "cursor-pointer" : "cursor-default opacity-60"
          }`}
        >
          <CheckTile checked={allVisibleSelected} indeterminate={someVisibleSelected} />
          <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#1A2332]">
            Select all
          </span>
        </div>

        {/* Users */}
        <div className="flex-1 overflow-y-auto pt-6 pb-3 pl-6 pr-7">
          {visibleUsers.length === 0 ? (
            <p className="py-8 text-center font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#868DA6]">
              No users match your search.
            </p>
          ) : (
            <div className="flex flex-col gap-5">
              {visibleUsers.map((user) => {
                const subtitle = subtitleOf(user);
                return (
                  <div
                    key={user.id}
                    onClick={() => toggleUser(user.id)}
                    className="w-full flex items-start gap-3 cursor-pointer"
                  >
                    <span className="py-[3px] shrink-0">
                      <CheckTile checked={selectedIds.has(String(user.id))} />
                    </span>
                    <span className="flex items-center gap-2 flex-wrap">
                      <span className="font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#1A2332]">
                        {user.name}
                      </span>
                      {subtitle && (
                        <span className="font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#7C829A]">
                          ({subtitle})
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-white border-t-[1px] border-[#E5E8F3] flex items-center justify-between gap-4">
          <span className="font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#7C829A]">
            {selectedIds.size} {selectedIds.size === 1 ? "user" : "users"} selected
          </span>
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="h-10 px-4 flex items-center justify-center bg-white rounded-[8px] outline outline-1 outline-[#C9CEE2] -outline-offset-1 shadow-[0px_1px_2px_rgba(16,24,40,0.05)] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#1A2332] hover:bg-[#F8F9FE] transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="h-10 px-4 min-w-[72px] flex items-center justify-center bg-blueColor rounded-[8px] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-white hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {saving ? <Loader variant="button" ariaLabel="saving-receivers" /> : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const FilterPill = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`h-7 px-3 py-1 rounded-[16px] flex items-center justify-center font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize transition-colors ${
      active
        ? "bg-blueColor text-white"
        : "bg-transparent text-[#1A2332] outline outline-1 outline-[#E5E8F3] -outline-offset-1 hover:bg-[#F8F9FE]"
    }`}
  >
    {label}
  </button>
);

export default ReceiversDrawer;
